import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button, Collapse, Select, Input,
} from 'antd';
import {
  arrayOf, bool, number, shape, string,
} from 'prop-types';
import {
  useDeferredValue, useMemo, useState, useEffect,
} from 'react';
import Fuse from 'fuse.js';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import MapComponent from 'components/map';
import { useI18n } from 'context/i18n';
import { useLocale } from 'hooks';

import styles from './style.module.css';

function calculateAverage(storesArray) {
  return storesArray
    .reduce(
      ([ lat, long ], store) => {
        const newCoords = [ lat + store.latitude, long + store.longitude ];
        return newCoords;
      },
      [ 0, 0 ],
    )
    .map((n) => n / storesArray.length);
}
export function NnormalStores({ stores: originalStores, isMobile }) {
  const [ query, setQuery ] = useState('');
  const deferredQuery = useDeferredValue(query);
  const { t } = useI18n();
  const { currentCountry } = useLocale();

  const stores = useMemo(() => originalStores.filter((s) => {
    if (!s.latitude || !s.longitude) {
      console.warn(`store with id ${s.id} does not have latitude or longitude`);
      return false;
    }
    return true;
  }), [ originalStores ]);

  const countries = useMemo(() => stores.reduce((acc, store) => {
    if (!acc[store.countryCode]) {
      acc[store.countryCode] = store.country;
    }
    return acc;
  }, {}), [ stores ]);

  const allStoresByCountry = useMemo(() => stores.reduce((acc, store) => {
    if (!acc[store.countryCode]) {
      acc[store.countryCode] = [ store ];
    } else {
      acc[store.countryCode].push(store);
    }
    return acc;
  }, {}), [ stores ]);

  const selectCountries = useMemo(() => Object.entries(countries).sort((a, b) => a[1].localeCompare(b[1])).map(([ value, label ]) => ({ value, label })), [ countries ]);

  const [ selectedCountry, setSelectedCountry ] = useState('');
  const [ selectedStore, setSelectedStore ] = useState(null);
  const [ viewMap, setViewMap ] = useState(false);
  const [ averageCenter, setAverageCenter ] = useState(() => {
    // if we find that the current country in the locale exists in the list we center by that country
    const storesInCurrentCountry = allStoresByCountry?.[currentCountry.toLowerCase()];
    if (Array.isArray(storesInCurrentCountry) && storesInCurrentCountry.length > 0) {
      return calculateAverage(storesInCurrentCountry);
    }
    // by default we calculate the center of all stores
    return calculateAverage(stores);
  });
  const [ zoom, setZoom ] = useState(5);
  const [ activeKey, setActiveKey ] = useState(undefined);

  // derived state
  const filteredStores = useMemo(() => {
    let filtered = stores;
    if (selectedCountry !== '') {
      filtered = allStoresByCountry?.[selectedCountry] ?? [];
    }
    if (deferredQuery.length > 1) {
      const fuse = new Fuse(filtered, { keys: [ 'country', 'cityName', 'postalCode', 'name', 'address1' ], threshold: 0.1 });
      filtered = fuse.search(deferredQuery).map((f) => f.item);
    }
    return filtered;
  }, [ deferredQuery, stores, selectedCountry ]);

  const filteredStoresByCountry = useMemo(() => filteredStores.reduce((acc, store) => {
    if (!acc[store.countryCode]) {
      acc[store.countryCode] = [ store ];
    } else {
      acc[store.countryCode].push(store);
    }
    return acc;
  }, {}), [ filteredStores ]);

  const showList = () => {
    setViewMap(false);
  };

  const toggleMap = () => {
    if (viewMap) showList();
    else setViewMap(true);
  };

  const handleSelectCountry = (value) => {
    setSelectedCountry(value);
    setActiveKey(value);
    if (allStoresByCountry?.[value] !== undefined) {
      setZoom(5);
      setSelectedStore(null);
      setAverageCenter(calculateAverage(allStoresByCountry[value]));
    }
  };

  const handleClickMarker = (coords) => {
    const foundStore = filteredStores.find((s) => s.latitude === coords[0] && s.longitude === coords[1]);
    if (foundStore) {
      setActiveKey(foundStore.countryCode);
      setSelectedStore(foundStore);

      if (isMobile) {
        showList();
      }
    }
  };
  const handleSelectStore = (store) => {
    if (isMobile) {
      setViewMap(true);
    }

    setZoom(12);
    setSelectedStore(store);
  };

  const MapMemo = useMemo(() => (
    <MapComponent
      sticky={false}
      center={selectedStore === null ? averageCenter : [ selectedStore.latitude, selectedStore.longitude ]}
      visible={true}
      viewStoreInMap={false}
      pins={filteredStores.map(({ latitude, longitude }) => ({
        latitude, longitude,
      }))}
      onClickMarker={handleClickMarker}
      markerProvider={{ iconUrl: '/assets-new/nn-locator-marker.svg', iconSize: [ 21, 32 ] }}
      selectedMarkerProvider={{ iconUrl: '/assets-new/nn-locator-marker-selected.svg', iconSize: [ 21, 32 ] }}
      zoom={zoom}
      onZoom={setZoom}
    />
  ), [ filteredStores, selectedStore, averageCenter ]);

  useEffect(() => {
    if (selectedStore) {
      setTimeout(() => {
        const foundDiv = document.querySelector(`[data-store="${selectedStore.id}"]`);
        // const foundList = document.getElementById('storesList');
        if (foundDiv) {
          foundDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        //   const bound = foundDiv.getBoundingClientRect();
        //   const listBound = foundList.getBoundingClientRect();
        //   const parentDivBound = foundDiv.parentNode.parentElement.getBoundingClientRect();
        }
      }, 500);
    }
  }
  , [ selectedStore ]);

  return (
    <main className={styles.container}>
      <div className={styles.storesContainer}>
        <Select value={selectedCountry} onChange={handleSelectCountry} options={[ { value: '', label: t('modal.idiomas', 'select.country', 'Select a country') }, ...selectCountries ]} suffixIcon={<ChevronSVG />} />
        <Input value={query} onChange={(e) => setQuery(e.currentTarget.value)} placeholder={t('tiendas.locator', 'filter.stores', 'Filtrar tiendas')} />
        <div className={styles.toggleButton}>
          <Button type="primary" onClick={toggleMap}>
            {viewMap ? t('tiendas.locator', 'view.list', 'View list') : t('tiendas.locator', 'view.map', 'View map')}
          </Button>
        </div>
        <div id="storesList" className={styles.list} data-hidden={viewMap}>
          <Collapse
            bordered={false}
            accordion
            activeKey={activeKey}
            onChange={setActiveKey}
            expandIcon={({ isActive }) => (isActive ? <MinusOutlined /> : <PlusOutlined />)}
            expandIconPosition="right"
            className={styles.customAccordion}
          >
            {Object.entries(filteredStoresByCountry).sort((a, b) => countries[a[0]].localeCompare(countries[b[0]])).map(([ countryKey, s ]) => (selectedCountry === '' || selectedCountry === countryKey ? (
              <Collapse.Panel forceRender header={countries[countryKey]} key={countryKey} id={countryKey}>
                <div className={styles.panelContainer} >
                  {s.sort((a, b) => a.cityName.localeCompare(b.cityName)).map((store) => (
                    <div key={`store-${store.id}`} data-store={store.id} data-active={selectedStore && selectedStore.id === store.id} onClick={() => handleSelectStore(store)}>
                      <p>{store.cityName}</p>
                      <p>{store.name}</p>
                      <p>{store.address1}</p>
                      <p>{store.telephone}</p>
                      <a href={store.urlGoogleMaps || `https://maps.google.com/?q=${store.latitude},${store.longitude}`} target="_blank" className={styles.viewInMap} rel="noreferrer">{t('tiendas.locator', 'map', 'Mapa')}</a>
                    </div>
                  ))}
                </div>
              </Collapse.Panel>
            ) : null))}
          </Collapse>
        </div>
      </div>
      <div className={styles.mapContainer} data-hidden={!viewMap}>
        <div className={styles.map} >
          {MapMemo}
        </div>
      </div>

    </main>
  );
}

NnormalStores.displayName = 'NnormalStores';

NnormalStores.propTypes = {
  stores: arrayOf(shape({
    id: string,
    name: string,
    address1: string,
    postalCode: string,
    cityName: string,
    latitude: number,
    longitude: number,
    telephone: string,
    bookingAvailability: bool,
    inStoreAppointment: bool,
    videoCallAppointment: bool,
    featMen: bool,
    featWomen: bool,
    featKids: bool,
    featBags: bool,
    storePickup: bool,
    status: string,
    statusText: string,
    imageMain: string,
    whatsappPhone: string,
    reportingCode: string,
    storeHours: arrayOf(shape({
      days: string,
      timetable: string,
    })),
    country: string,
    countryCode: string,
    urlGoogleMaps: string,
    urlGoogleReviewsList: string,
    regionName: string,
    url: string,
    franchise: bool,
  })),
  isMobile: bool,
};
